import { useCallback, useEffect, useRef, useState } from "react";
import { FintracPagination } from "@services/models/Fintrac/FintracModel";
import { normalizePagination, readFintracError } from "./fintracFormat";

const PAGE_SIZE = 20;

const EMPTY_PAGINATION: FintracPagination = {
  page: 1,
  limit: PAGE_SIZE,
  totalRecords: 0,
  totalPages: 0,
};

type ListResult<T> = {
  rows: T[];
  pagination?: Partial<FintracPagination>;
};

const mergeRows = <T extends { id: number }>(current: T[], incoming: T[]) => {
  const seen = new Set(current.map((row) => row.id));
  const next = incoming.filter((row) => !seen.has(row.id));
  return next.length === 0 ? current : [...current, ...next];
};

export const useFintracList = <T extends { id: number }>(
  resetKey: string,
  load: (page: number, limit: number) => Promise<ListResult<T>>,
) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<T[]>([]);
  const [pagination, setPagination] =
    useState<FintracPagination>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const applyPage = (
    nextPage: number,
    nextPagination: FintracPagination,
    rowCount: number,
  ) => {
    pageRef.current = nextPage;
    setPage(nextPage);
    setPagination(nextPagination);
    hasMoreRef.current =
      rowCount > 0 &&
      nextPagination.totalPages > 0 &&
      nextPage < nextPagination.totalPages;
  };

  useEffect(() => {
    const requestGeneration = generation.current + 1;
    generation.current = requestGeneration;
    let cancelled = false;
    pageRef.current = 1;
    setPage(1);
    setLoading(true);
    setLoadingMore(false);
    setError("");
    inFlight.current = true;

    loadRef
      .current(1, PAGE_SIZE)
      .then((result) => {
        if (cancelled || generation.current !== requestGeneration) return;
        const nextPagination = normalizePagination(
          result.pagination,
          1,
          PAGE_SIZE,
        );
        setRows(result.rows);
        applyPage(1, nextPagination, result.rows.length);
      })
      .catch((err) => {
        if (cancelled || generation.current !== requestGeneration) return;
        setRows([]);
        applyPage(1, normalizePagination(undefined, 1, PAGE_SIZE), 0);
        setError(readFintracError(err));
      })
      .finally(() => {
        if (cancelled || generation.current !== requestGeneration) return;
        inFlight.current = false;
        setLoading(false);
      });

    return () => {
      cancelled = true;
      inFlight.current = false;
    };
  }, [resetKey]);

  const loadMore = useCallback(() => {
    if (inFlight.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    const requestGeneration = generation.current;
    inFlight.current = true;
    setLoadingMore(true);
    setError("");

    loadRef
      .current(nextPage, PAGE_SIZE)
      .then((result) => {
        if (generation.current !== requestGeneration) return;
        const nextPagination = normalizePagination(
          result.pagination,
          nextPage,
          PAGE_SIZE,
        );
        setRows((current) => mergeRows(current, result.rows));
        applyPage(nextPage, nextPagination, result.rows.length);
      })
      .catch((err) => {
        if (generation.current !== requestGeneration) return;
        setError(readFintracError(err));
      })
      .finally(() => {
        if (generation.current !== requestGeneration) return;
        inFlight.current = false;
        setLoadingMore(false);
      });
  }, []);

  const refetch = useCallback(async (options?: { quiet?: boolean }) => {
    const requestGeneration = generation.current;
    const pagesToLoad = Math.max(pageRef.current, 1);
    if (!options?.quiet) setLoading(true);
    setError("");
    inFlight.current = true;

    try {
      const collected: T[] = [];
      const seen = new Set<number>();
      let last = normalizePagination(undefined, 1, PAGE_SIZE);
      let loadedThrough = 1;

      for (let nextPage = 1; nextPage <= pagesToLoad; nextPage += 1) {
        if (generation.current !== requestGeneration) return;
        if (nextPage > 1 && last.totalPages > 0 && nextPage > last.totalPages) {
          break;
        }
        const result = await loadRef.current(nextPage, PAGE_SIZE);
        if (generation.current !== requestGeneration) return;
        last = normalizePagination(result.pagination, nextPage, PAGE_SIZE);
        result.rows.forEach((row) => {
          if (seen.has(row.id)) return;
          seen.add(row.id);
          collected.push(row);
        });
        loadedThrough = nextPage;
        if (result.rows.length === 0 || nextPage >= last.totalPages) break;
      }

      if (generation.current !== requestGeneration) return;
      applyPage(loadedThrough, last, collected.length);
      setRows(collected);
    } catch (err) {
      if (generation.current !== requestGeneration) return;
      if (!options?.quiet) {
        setRows([]);
        applyPage(1, normalizePagination(undefined, 1, PAGE_SIZE), 0);
      }
      setError(readFintracError(err));
    } finally {
      if (generation.current === requestGeneration) {
        inFlight.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  return {
    rows,
    setRows,
    pagination,
    loading,
    loadingMore,
    error,
    hasMore: pagination.totalPages > 0 && page < pagination.totalPages,
    loadMore,
    refetch,
  };
};
